import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Verification token is missing');
      setVerifying(false);
      return;
    }

    // Verify token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/verify-email`, {
          params: { token }
        });
        setUserInfo(response.data);
        setVerifying(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Invalid or expired verification token');
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd);
    
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords
    if (!password || !confirmPassword) {
      setError('Please enter and confirm your password');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/set-password`, {
        token,
        password,
        confirmPassword
      });

      // Store token and check if 2FA is required
      localStorage.setItem('adminToken', response.data.token);
      setSuccess('Email verified and password set successfully! Redirecting...');
      
      // Check if user has 2FA enabled
      try {
        const userResponse = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${response.data.token}`
          }
        });
        
        if (!userResponse.data.twoFactorEnabled) {
          // User must enable 2FA before accessing the dashboard
          setTimeout(() => {
            navigate('/require-2fa');
          }, 1500);
        } else {
          setTimeout(() => {
            navigate('/dashboard');
          }, 1500);
        }
      } catch (err) {
        // If we can't check 2FA status, redirect to require-2fa to be safe
        setTimeout(() => {
          navigate('/require-2fa');
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set password. Please try again.');
      setLoading(false);
    }
  };

  const cardStyle = {
    width: '100%',
    maxWidth: '450px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  };

  const header = (
    <div style={{ textAlign: 'center', padding: '1.5rem' }}>
      <h2 style={{ 
        margin: 0, 
        fontSize: '1.75rem', 
        fontWeight: '600',
        color: '#2c3e50',
        marginBottom: '0.5rem'
      }}>
        Verify Your Email
      </h2>
      <p style={{ 
        margin: 0, 
        color: '#6c757d',
        fontSize: '0.95rem'
      }}>
        Set your password to complete registration
      </p>
    </div>
  );

  if (verifying) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        backgroundColor: '#f5f6fa', 
        padding: '1.5rem'
      }}>
        <Card style={cardStyle}>
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <ProgressSpinner />
            <p style={{ marginTop: '1.5rem', color: '#6c757d' }}>Verifying token...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!userInfo && error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        backgroundColor: '#f5f6fa', 
        padding: '1.5rem'
      }}>
        <Card style={cardStyle} header={header}>
          <div style={{ padding: '2rem' }}>
            <Message 
              severity="error" 
              text={error} 
              style={{ marginBottom: '1.5rem' }} 
            />
            <Button
              label="Go to Login"
              icon="pi pi-sign-in"
              iconPos="left"
              onClick={() => navigate('/login')}
              style={{ width: '100%' }}
              className="p-button-lg"
            />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      backgroundColor: '#f5f6fa', 
      padding: '1.5rem'
    }}>
      <Card style={cardStyle} header={header}>
        <div style={{ padding: '2rem' }}>
          {userInfo && (
            <div style={{ 
              background: '#e7f3ff', 
              border: '1px solid #b3d9ff', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, color: '#0056b3', fontWeight: '500' }}>
                Hello, {userInfo.name}!
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: '#0056b3', fontSize: '0.9rem' }}>
                {userInfo.email}
              </p>
            </div>
          )}

          {error && (
            <Message 
              severity="error" 
              text={error} 
              style={{ marginBottom: '1.5rem' }} 
              onClose={() => setError('')} 
            />
          )}

          {success && (
            <Message 
              severity="success" 
              text={success} 
              style={{ marginBottom: '1.5rem' }} 
            />
          )}

          <form onSubmit={handleSubmit}>
            <div className="p-field" style={{ marginBottom: '1.5rem' }}>
              <label 
                htmlFor="password" 
                className="p-d-block" 
                style={{ 
                  marginBottom: '0.75rem', 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}
              >
                Password *
              </label>
              <Password
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                style={{ width: '100%' }}
                inputStyle={{ 
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  height: 'auto',
                  lineHeight: '1.5'
                }}
                feedback
                toggleMask
                disabled={loading}
                placeholder="Enter password"
                className="p-inputtext-lg"
              />
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d', fontSize: '0.85rem' }}>
                Password must be at least 8 characters and contain uppercase, lowercase, number, and special character
              </small>
            </div>

            <div className="p-field" style={{ marginBottom: '1.5rem' }}>
              <label 
                htmlFor="confirmPassword" 
                className="p-d-block" 
                style={{ 
                  marginBottom: '0.75rem', 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}
              >
                Confirm Password *
              </label>
              <Password
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                style={{ width: '100%' }}
                inputStyle={{ 
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  height: 'auto',
                  lineHeight: '1.5'
                }}
                toggleMask
                disabled={loading}
                placeholder="Confirm password"
                className="p-inputtext-lg"
              />
            </div>

            <Button
              type="submit"
              label="Verify Email & Set Password"
              icon={loading ? null : "pi pi-check"}
              iconPos="left"
              loading={loading}
              disabled={loading || !password || !confirmPassword}
              style={{ width: '100%', marginTop: '1rem' }}
              className="p-button-lg"
            />
          </form>
        </div>
      </Card>
    </div>
  );
};

export default VerifyEmail;

